// The plan on Billing, and the check before a plan changes (Brúnó,
// 2026-10-10: a brand switches its plan or buys more credit where it works).
// Paid through Stripe, each paid month of the plan comes back as the same credit; a change applies at once, and the difference is settled on the
// next invoice (surfaces/brand/billing.ts choosePaidPlan).

import {useEffect} from 'react';
import {Link} from 'react-router-dom';
import type {BrandAppData, BrandBilling, BrandPlan} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';

export function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 ? 2 : 0}).format(cents / 100);
}

function day(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short'});
}

function campaigns(plan: BrandPlan): string {
  return plan.campaign_limit === null ? 'Unlimited campaigns' : `${plan.campaign_limit} active campaign${plan.campaign_limit === 1 ? '' : 's'} at a time`;
}

/** Where the paid plan stands, in a few words; empty for a plan the team set by hand. */
function renewal(subscription: BrandBilling['subscription']): string {
  if (!subscription) return '';
  if (subscription.status === 'past_due' || subscription.status === 'unpaid') return 'The last payment failed: update your card';
  if (!subscription.renews_at) return '';
  return subscription.cancels ? `Ends ${day(subscription.renews_at)}` : `Renews ${day(subscription.renews_at)}`;
}

/** The plan in one line, at the top of Billing, with the way to change it. */
export function PlanLine({data, subscription}: {data: BrandAppData; subscription: BrandBilling['subscription']}) {
  const plan = data.plans.find((item) => item.key === data.plan.key);
  const status = renewal(subscription);
  // The plan is an Admin's; a Manager sees where it stands.
  const admin = data.me.role === 'admin';
  return <div className="plan-line">
    <span className="billing-icon"><Icon name="star" size={18} /></span>
    <div className="plan-line-main">
      {plan
        ? <><b>{plan.name} <small>{usd(plan.monthly_price_cents)} / month</small></b>
          <span>{[status, data.billing.payments_enabled ? `${usd(plan.monthly_price_cents)} credit every month` : '', campaigns(plan)].filter(Boolean).join(' · ')}</span></>
        : <><b>No plan yet</b><span>Choose one to start campaigns.</span></>}
    </div>
    {admin ? <Link className={`btn btn-sm ${plan ? 'btn-light' : 'btn-g'}`} to="/plan">{plan ? 'Change plan' : 'Choose a plan'}</Link> : null}
  </div>;
}

/** The check before a paid plan changes: what the new plan costs and gives, and when the money moves. */
export function PlanSwitchDialog({from, to, activeCampaigns, busy, onCancel, onConfirm}: {
  from: BrandPlan; to: BrandPlan; activeCampaigns: number; busy: boolean; onCancel: () => void; onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const upgrade = to.monthly_price_cents > from.monthly_price_cents;
  const over = to.campaign_limit !== null && activeCampaigns > to.campaign_limit;
  return <div className="switch-layer">
    <div className="switch-backdrop" onClick={onCancel} />
    <div className="card switch-dialog" role="dialog" aria-modal="true" aria-labelledby="switch-title">
      <button className="switch-close" type="button" onClick={onCancel} aria-label="Close"><Icon name="x" size={18} /></button>
      <p className="eyebrow">Change plan</p>
      <h2 id="switch-title">Switch to {to.name}?</h2>
      <dl className="switch-rows">
        <div><dt>Price</dt><dd>{usd(to.monthly_price_cents)} / month <s>{usd(from.monthly_price_cents)}</s></dd></div>
        <div><dt>Credit</dt><dd>{usd(to.monthly_price_cents)} every month</dd></div>
        <div><dt>Campaigns</dt><dd>{campaigns(to)}</dd></div>
        <div><dt>Support</dt><dd>{to.setup_help ? 'Help setting up every campaign' : 'Standard support'}</dd></div>
      </dl>
      <p className="switch-note"><Icon name="info" size={16} />{upgrade
        ? 'It applies now. The difference for the rest of this month is on your next invoice, and it comes back as credit when it’s paid.'
        : 'It applies now. What you paid for the rest of this month comes off your next invoices, and your credit stays.'}</p>
      {over ? <p className="plan-warn">Your {activeCampaigns} active campaigns keep running. A new one can be published once fewer than {to.campaign_limit} {to.campaign_limit === 1 ? 'is' : 'are'} active.</p> : null}
      <div className="switch-actions">
        <button className="btn btn-light" type="button" onClick={onCancel}>Keep {from.name}</button>
        <button className="btn btn-g" type="button" disabled={busy} onClick={onConfirm}>{busy ? 'Switching…' : `Switch to ${to.name}`}</button>
      </div>
    </div>
  </div>;
}
